'use client';

import { useRef, useState, type PointerEvent } from 'react';

import styled from '@emotion/styled';
import { cn } from 'cn';

import { isTapGesture } from '@/shared-mobile-app';
import { getVoiceOrbState, VoiceOrb } from '@/shared-ui';

import {
  clampPillPosition,
  type Point,
} from './project-usability-mobile-controls.helpers';

interface Props {
  orbState: ReturnType<typeof getVoiceOrbState>;
  audioLevel: number;
  progress: number;
  onOpen: () => void;
}

interface DragState {
  pointerId: number;
  originX: number;
  originY: number;
  startX: number;
  startY: number;
  moved: boolean;
  captured: boolean;
}

// reuse-exception: needs a native <button> for reliable activation through Hyperbeam
// forwarded input, styled+positioned as the draggable pointer-capture target — the shared Button cannot be that element.
const HubContainer = styled.button<{ position: Point | null }>`
  ${(props) =>
    props.position
      ? `top: ${props.position.y}px; left: ${props.position.x}px; right: auto; bottom: auto;`
      : ''}
`;

// test-exception: pointer-capture drag flow needs @testing-library/react (absent); tap/clamp logic is covered by project-usability-mobile-controls.helpers.spec.ts
export default function ProjectUsabilityMobileVoiceHub({
  orbState,
  audioLevel,
  progress,
  onOpen,
}: Props) {
  const hubRef = useRef<HTMLButtonElement>(null);
  const dragRef = useRef<DragState | null>(null);
  const suppressClickRef = useRef(false);

  const [position, setPosition] = useState<Point | null>(null);

  const handlePointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    const element = hubRef.current;
    if (!element) return;

    suppressClickRef.current = false;

    const rect = element.getBoundingClientRect();
    dragRef.current = {
      pointerId: event.pointerId,
      originX: rect.left,
      originY: rect.top,
      startX: event.clientX,
      startY: event.clientY,
      moved: false,
      captured: false,
    };
  };

  const handlePointerMove = (event: PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    const element = hubRef.current;
    if (!drag || !element) return;

    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    if (!drag.moved && isTapGesture(dx, dy)) return;
    drag.moved = true;

    if (!drag.captured) {
      // Capture only once a drag begins so a throwing/lost capture never blocks tap-to-open.
      try {
        element.setPointerCapture(event.pointerId);
        drag.pointerId = event.pointerId;
        drag.captured = true;
      } catch {
        drag.captured = false;
      }
    }

    const rect = element.getBoundingClientRect();
    const next = clampPillPosition(
      { x: drag.originX + dx, y: drag.originY + dy },
      { width: window.innerWidth, height: window.innerHeight },
      { width: rect.width, height: rect.height }
    );
    setPosition(next);
  };

  const handlePointerUp = (event: PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    const element = hubRef.current;
    if (!drag) return;

    dragRef.current = null;
    if (drag.captured && element) {
      try {
        element.releasePointerCapture(drag.pointerId);
      } catch {
        // Capture may already be released; releasing must not block the tap.
      }
    }

    // Tap vs drag by net displacement at release — not a mid-gesture flag or strict pointerId:
    // Hyperbeam-forwarded taps jitter, renumber pointer IDs, and never fire a click.
    if (
      isTapGesture(event.clientX - drag.startX, event.clientY - drag.startY)
    ) {
      onOpen();
    } else {
      suppressClickRef.current = true;
    }
  };

  const handleClick = () => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    onOpen();
  };

  return (
    <HubContainer
      ref={hubRef}
      type='button'
      position={position}
      aria-label='Open task panel'
      data-test='mobile-voice-hub'
      className={cn(
        'fixed z-40 flex touch-none appearance-none flex-col items-center gap-2 rounded-[64px] border border-gray-300 bg-gray-900 px-2 pt-3 pb-2 select-none',
        'right-[calc(env(safe-area-inset-right)+1rem)] bottom-[calc(env(safe-area-inset-bottom)+1rem)]',
        'drop-shadow-[0_4px_24px_#34346F3D] focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:outline-none'
      )}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onClick={handleClick}
    >
      <span
        className='h-1.25 w-5.5 rounded-full bg-gray-700'
        aria-hidden='true'
      />
      <VoiceOrb
        state={orbState}
        audioLevel={audioLevel}
        progress={progress}
        compact
      />
    </HubContainer>
  );
}
